import { FormikConfig } from 'formik';
import produce, { current } from 'immer';
import { isEmpty } from 'lodash-es';
import React, { useContext, useMemo, useReducer } from 'react';

import * as errors from 'utils/errors';

import * as Interfaces from './interfaces';

/**
 * Create a context object
 */
const WizardContext = React.createContext({} as Interfaces.WizardStepContext);

const defaultDelayedLoadingMs = 500;

/**
 * Context initial state
 */
export const initialState: (
  config: Pick<FormikConfig<any>, 'initialErrors' | 'initialTouched' | 'initialValues'>
) => Interfaces.ContextState = config => ({
  error: undefined,
  currentStepId: '',
  steps: {},
  formValues: config.initialValues,
  formErrors: config.initialErrors,
  formTouched: config.initialTouched,
  version: 1,
  delayedLoadingMs: defaultDelayedLoadingMs,
  mode: 'Normal',
  status: 'idle'
});

/**
 * The Wizard Reducer object
 * @param state State of the Wizard internal <Page /> component
 * @param action action to modify the <Page /> display settings
 */
const reducer: React.Reducer<Interfaces.ContextState, Interfaces.WizardStepAction<any>> = (
  state,
  action
) => {
  return produce(state, draft => {
    /**
     * Helper method to evaluate the "draft" Proxy object with the action values
     * @param values the action values
     */
    const setWizardStateChanges = (data: { values?: any; touched?: any; errors?: any }) => {
      // User supplied values data to the action (anything but null / undefined)
      const { values, errors, touched } = data;

      // In type object needed to spread the "values" of the wizard (most cases this will be like that)
      if (values) {
        if (typeof values === 'object') {
          draft.formValues = {
            ...draft.formValues,
            ...values
          };
        } else {
          draft.formValues = values;
        }
      }

      // In type object needed to spread the "errors" of the wizard (most cases this will be like that)
      if (errors) {
        if (typeof errors === 'object') {
          draft.formErrors = {
            ...draft.formErrors,
            ...errors
          };
        } else {
          draft.formErrors = errors;
        }
      }

      // In type object needed to spread the "touched" of the wizard (most cases this will be like that)
      if (touched) {
        if (typeof touched === 'object') {
          draft.formTouched = {
            ...draft.formTouched,
            ...touched
          };
        } else {
          draft.formTouched = touched;
        }
      }
      // update the version on values changes (gets persisted to storage data prop in the "usePersist" custom hook)
      draft.version = draft.version + 1;
    };

    switch (action.type) {
      case 'API_REQUEST':
        draft.error = action.error;
        draft.status = action.status;
        break;
      case 'UPDATE_STEP':
        if (isEmpty(current(draft.steps[action.stepId]))) {
          throw new errors.SVError(
            `Given step id ${action.stepId} was not found in the wizard steps collection`
          );
        }
        let isUpdated = false;
        if (action.title) {
          draft.steps[action.stepId].title = action.title;
          isUpdated = true;
        }
        if (action.navigation) {
          draft.steps[action.stepId].navigation = action.navigation;
          isUpdated = true;
        }
        if (action.stepName) {
          draft.steps[action.stepId].stepName = action.stepName;
          isUpdated = true;
        }
        if (action.validation) {
          draft.steps[action.stepId].validation = action.validation;
          isUpdated = true;
        }
        if (isUpdated) {
          draft.version = draft.version + 1;
        }
        break;
      case 'STEP_TRANSITION':
        draft.error = undefined;
        draft.status = action.loading ? 'pending' : 'idle';
        break;
      case 'SET_FORM_PROPS':
        setWizardStateChanges({
          values: action.values,
          errors: action.errors,
          touched: action.touched
        });
        break;
      case 'INIT_VALUES':
        setWizardStateChanges({ values: action.values });
        break;
      case 'GOTO_STEP':
        if (isEmpty(current(draft.steps[action.stepId]))) {
          throw new errors.SVError(
            `Given step id ${action.stepId} was not found in the wizard steps collection`
          );
        }
        draft.currentStepId = action.stepId;
        draft.status = 'pending';
        draft.delayedLoadingMs = action.delayedLoadingMs ?? defaultDelayedLoadingMs;
        setWizardStateChanges({ values: action.values });
        break;
      case 'CHANGE_MODE':
        if (isEmpty(current(draft.steps[action.stepId]))) {
          throw new errors.SVError(
            `Given step id ${action.stepId} was not found in the wizard steps collection`
          );
        }
        draft.mode = action.mode;
        draft.currentStepId = action.stepId;
        draft.delayedLoadingMs = action.delayedLoadingMs ?? defaultDelayedLoadingMs;
        setWizardStateChanges({ values: action.values });
    }
  });
};

/**
 * Get the Wizard context provider with the different page state data used across the entire Wizard steps
 */
export const WizardProvider: React.FC<Interfaces.WizardProviderProps> = ({ children, ...rest }) => {
  const [state, dispatch] = useReducer(reducer, rest);

  /**
   * Use useMemo for optimization reasons. This says don't give back a new state unless state changes
   * https://github.com/kentcdodds/kentcdodds.com/blob/319db97260078ea4c263e75166f05e2cea21ccd1/content/blog/how-to-optimize-your-context-value/index.md
   * https://blog.isquaredsoftware.com/2020/05/blogged-answers-a-mostly-complete-guide-to-react-rendering-behavior/#context-updates-and-render-optimizations
   */
  const value: Interfaces.WizardStepContext = useMemo(
    () => ({
      dispatch,
      steps: state.steps,
      appStates: rest.appStates,
      error: state.error,
      currentStepId: state.currentStepId,
      formValues: state.formValues,
      formErrors: state.formErrors,
      formTouched: state.formTouched,
      mode: state.mode,
      version: state.version,
      delayedLoadingMs: state.delayedLoadingMs,
      isIdle: state.status === 'idle' || state.status === 'resolved',
      isPending: state.status === 'pending',
      isRejected: state.status === 'rejected'
    }),
    [
      rest.appStates,
      state.currentStepId,
      state.delayedLoadingMs,
      state.error,
      state.mode,
      state.status,
      state.steps,
      state.formValues,
      state.formErrors,
      state.formTouched,
      state.version
    ]
  );
  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

/**
 * instead of calling useContext directly in our components,  we make our own hook that throws an error if we try to access context outside of the provider
 */
export function useWizardContext() {
  const context = useContext(WizardContext);
  if (!context) {
    throw new errors.SVError('WizardContext must be used with WizardProvider!');
  }
  return context;
}
